/**
 * Funções HTTP da assinatura ICP-Brasil do laudo (E164.F2, ADR-0113).
 *
 * Preparar devolve o PDF emitido (base64) para o assinador local; concluir manda o PDF assinado e
 * recebe o estado da cadeia. O erro carrega o `detail` do servidor, que a tela traduz.
 */
import axios, { type AxiosInstance } from 'axios';

import type { ChecklistRevisao } from '@/lib/certificacao';

export type PapelNaAssinatura = 'assistente_tecnico' | 'responsavel_tecnico';

export interface PdfParaAssinar {
  laudo_assinatura_id: string;
  proximo_papel: PapelNaAssinatura;
  documento_sha256: string;
  nome_do_arquivo: string;
  pdf_base64: string;
}

export interface EstadoDaAssinaturaIcp {
  laudo_assinatura_id: string;
  requisicao_id: string;
  case_id: string;
  estado: 'aguardando_assistente' | 'aguardando_responsavel' | 'assinada';
  proximo_papel: PapelNaAssinatura | null;
  proximo_usuario_id: string | null;
  documento_sha256: string;
  reconciliada: boolean;
}

export interface PendenteDoResponsavel {
  requisicao_id: string;
  laudo_assinatura_id: string;
  case_id: string;
  display_code: string;
  source_document_id: string;
  assistente_nome: string | null;
  assinada_pelo_assistente_em: string;
}

/** `codigo`: o `detail` do servidor, `rede` (a resposta não chegou) ou `desconhecido`. */
export class AssinaturaIcpHttpError extends Error {
  public readonly codigo: string;

  constructor(codigo: string) {
    super(codigo);
    this.codigo = codigo;
    this.name = 'AssinaturaIcpHttpError';
  }
}

function classificar(err: unknown): AssinaturaIcpHttpError {
  if (!axios.isAxiosError(err)) return new AssinaturaIcpHttpError('desconhecido');
  const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string') return new AssinaturaIcpHttpError(detail);
  if (err.response === undefined) return new AssinaturaIcpHttpError('rede');
  return new AssinaturaIcpHttpError('desconhecido');
}

const base = (requisicaoId: string) => `/certificacao/requisicoes/${requisicaoId}`;

export async function prepararAssinatura(
  client: AxiosInstance,
  requisicaoId: string,
  checklist: ChecklistRevisao,
): Promise<PdfParaAssinar> {
  try {
    const { data } = await client.post<PdfParaAssinar>(
      `${base(requisicaoId)}/preparar-assinatura`,
      { checklist },
    );
    return data;
  } catch (err) {
    throw classificar(err);
  }
}

export async function pdfDaVez(
  client: AxiosInstance,
  requisicaoId: string,
): Promise<PdfParaAssinar> {
  try {
    const { data } = await client.get<PdfParaAssinar>(`${base(requisicaoId)}/assinatura-icp/pdf`);
    return data;
  } catch (err) {
    throw classificar(err);
  }
}

async function enviar(
  client: AxiosInstance,
  requisicaoId: string,
  pdfBase64: string,
): Promise<EstadoDaAssinaturaIcp> {
  try {
    const { data } = await client.post<EstadoDaAssinaturaIcp>(
      `${base(requisicaoId)}/assinatura-icp`,
      { pdf_base64: pdfBase64 },
    );
    return data;
  } catch (err) {
    throw classificar(err);
  }
}

/**
 * Manda o PDF assinado. Sem resposta (rede), manda o MESMO arquivo de novo: o servidor reconhece
 * os bytes já gravados e devolve o estado (`reconciliada`), sem assinar duas vezes.
 */
export async function concluirAssinatura(
  client: AxiosInstance,
  requisicaoId: string,
  pdfBase64: string,
): Promise<EstadoDaAssinaturaIcp> {
  try {
    return await enviar(client, requisicaoId, pdfBase64);
  } catch (err) {
    if (err instanceof AssinaturaIcpHttpError && err.codigo === 'rede') {
      return enviar(client, requisicaoId, pdfBase64);
    }
    throw err;
  }
}

export async function assinaturasPendentes(
  client: AxiosInstance,
): Promise<PendenteDoResponsavel[]> {
  try {
    const { data } = await client.get<PendenteDoResponsavel[]>(
      '/certificacao/assinaturas-pendentes', // ux-neutrality:allow contrato-servidor
    );
    return data;
  } catch (err) {
    throw classificar(err);
  }
}
